import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Mobile keyboards only open when a text input is focused synchronously
    // inside the user gesture (the tap). Deferring showModal() to the rAF
    // below pushes focus into a later task, which leaves the search modal's
    // input focused but the keyboard closed. A dialog that marks its input
    // with [autofocus] (the search modal) therefore opens synchronously and
    // the input is focused explicitly, still inside the gesture.
    const autofocusInput = dialog.querySelector('input[autofocus]');

    const open = () => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Fixing the body just collapsed the document's scrollable extent, but
      // the scroll offset only clamps to 0 on the next layout pass. If the
      // keyboard opens while window.scrollY still reads the old page offset,
      // iOS Safari's "reveal the focused input" pass computes its scroll
      // target from that stale offset and, with nothing left to scroll,
      // applies the remainder as a visual-viewport pan — shoving the dialog
      // header (and the input) off the top of the screen with no way to
      // scroll it back. Settle the clamp synchronously so the reveal has
      // nothing to do. (A no-op on engines that already clamped.)
      if (autofocusInput) window.scrollTo(0, 0);

      dialog.showModal();
      if (autofocusInput instanceof HTMLElement) {
        autofocusInput.focus({ preventScroll: true });
      } else if (!dialog.querySelector('[autofocus]')) {
        // showModal() focuses the dialog's first focusable element (e.g. the
        // cart drawer's close button), and iOS Safari treats that
        // dialog-initiated focus as keyboard-like — so :focus-visible matches
        // even for a tap and paints the circular focus ring around the close
        // button. The updated HTML spec (and newer Chrome) instead focuses the
        // dialog itself when no descendant carries [autofocus]; emulate that
        // here so the ring only appears once a keyboard user actually tabs to
        // a control. Dialogs that opt into a focused control via [autofocus]
        // keep the native behavior.
        if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
        dialog.focus({ preventScroll: true });
      }
      this.dispatchEvent(new DialogOpenEvent());

      // Arm the outside-click / escape listeners on the next tick. If we add
      // them synchronously here, the same user gesture that opened the dialog
      // can still be in flight and get caught by #handleClick, which evaluates
      // it as a click outside the dialog and immediately calls closeDialog().
      // When that close races the open, the dialog flips shut before its closing
      // animation runs, so closeDialog()'s early `if (!dialog.open) return` skips
      // restoring body styles — leaving the page permanently scroll-locked.
      setTimeout(() => {
        if (!dialog.open) return;
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      }, 0);
    };

    if (autofocusInput) {
      open();
    } else {
      // Prevent layout thrashing by separating DOM reads from DOM writes
      requestAnimationFrame(open);
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    try {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    } finally {
      // The close MUST always complete. If scroll restore throws (an
      // extension patching window.scrollTo, engine quirks), bailing out here
      // would strand the dialog [open] with .dialog-closing — the finished
      // close animation (fill: forwards) holds it at opacity 0 while its
      // backdrop keeps painting, so the shopper sees a dimmed page with no
      // modal, and every re-open dead-ends on showDialog's `dialog.open`
      // early return until reload.
      dialog.close();
      dialog.classList.remove('dialog-closing');

      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
